/**
 * use-quick-replies.ts — Thư viện trả lời nhanh (Hội thoại: nút ⚡ + popup quản lý, đường dẫn /conversations/quick-replies).
 *
 * Bám modules/quick-replies/quick-reply-routes.ts. Dữ liệu chung bảng với "Mẫu tin"
 * của Tự động hoá (MessageTemplate) — sửa ở đây thì khối "Gửi mẫu tin" cũng thấy.
 * Ô soạn tin (quick-messages-popover) đọc cùng query này.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api-client'

export interface QuickReply {
  id: string
  title: string
  content: string
  category: string | null
  /** true = mẫu cá nhân (chỉ mình thấy); false = dùng chung cả công ty. */
  isPersonal: boolean
  createdBy: { id: string; fullName: string } | null
  createdAt: string
  updatedAt: string
}

export interface QuickReplyList {
  items: QuickReply[]
  categories: string[]
  /** Có quyền tạo mẫu dùng chung (= can.create). */
  canManageTeam: boolean
  /** Quyền với mẫu DÙNG CHUNG (quick_replies.create / update / delete / import). Mẫu cá nhân luôn tự quản lý được. */
  can?: { create: boolean; update: boolean; delete: boolean; import: boolean }
}

export interface QuickReplyInput {
  title: string
  content: string
  category?: string | null
  isPersonal?: boolean
}

export interface ImportResult {
  created: number
  updated: number
  skipped: { row: number; title: string; reason: string }[]
  errors: { row: number; message: string }[]
}

export const quickReplyKeys = { all: ['quick-replies'] as const }

/** Quyền "Dùng trả lời nhanh" (backend permission-registry) — thiếu thì ẩn nút ⚡ và chặn đường dẫn /conversations/quick-replies. */
export const QUICK_REPLIES_PERM = 'quick_replies.view'

export function useQuickReplies(enabled = true) {
  return useQuery<QuickReplyList>({
    queryKey: quickReplyKeys.all,
    enabled,
    staleTime: 60_000,
    queryFn: async () => (await api.get<QuickReplyList>('/quick-replies')).data,
  })
}

function useInvalidate() {
  const qc = useQueryClient()
  // Mẫu tin của Tự động hoá đọc cùng bảng → làm mới luôn.
  return () =>
    Promise.all([
      qc.invalidateQueries({ queryKey: quickReplyKeys.all }),
      qc.invalidateQueries({ queryKey: ['automation', 'templates'] }),
    ])
}

export function useSaveQuickReply() {
  const invalidate = useInvalidate()
  return useMutation<void, unknown, { id?: string; input: QuickReplyInput }>({
    mutationFn: async ({ id, input }) => {
      if (id) await api.put(`/quick-replies/${id}`, input)
      else await api.post('/quick-replies', input)
    },
    onSuccess: () => invalidate(),
  })
}

export function useDeleteQuickReply() {
  const invalidate = useInvalidate()
  return useMutation<void, unknown, string>({
    mutationFn: async (id) => {
      await api.delete(`/quick-replies/${id}`)
    },
    onSuccess: () => invalidate(),
  })
}

export function useBulkDeleteQuickReplies() {
  const invalidate = useInvalidate()
  return useMutation<{ deleted: number; forbidden: number; notFound: number }, unknown, string[]>({
    mutationFn: async (ids) => (await api.post('/quick-replies/bulk-delete', { ids })).data,
    onSuccess: () => invalidate(),
  })
}

export function useImportQuickReplies() {
  const invalidate = useInvalidate()
  return useMutation<
    ImportResult,
    unknown,
    {
      rows: { title: string; content: string; category: string | null }[]
      onDuplicate: 'skip' | 'overwrite'
      isPersonal: boolean
    }
  >({
    mutationFn: async (body) => (await api.post<ImportResult>('/quick-replies/import', body)).data,
    onSuccess: () => invalidate(),
  })
}
